import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";

import { deleteMessage } from "@/api/message/message.api";
import { createCitations } from "@/utils/util";

export const useChatAPI = ({
  controllerRef,
  setChatLoading,
  setStopReason,
  copilotStore,
}: any) => {
  const queryClient = useQueryClient();
  const [messageId, setMessageId] = useState("");
  const currentMessageId = useRef("");
  const currentConversationId = useRef("");
  const fallbackTimeoutRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, [copilotStore.uuid]);

  const sendChatMessage = useCallback(
    async (data: any, onNewConversation: (conversationId: string) => void) => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      currentConversationId.current = data.uuid ?? copilotStore.uuid;

      const fallbackMethod = async () => {
        if (
          copilotStore.uuid &&
          currentConversationId.current === copilotStore.uuid
        ) {
          controllerRef.current.abort();
          copilotStore.addMessage({
            message_id: "fallback",
            message_type: "System",
            data: {
              content:
                "Sorry, something went wrong while processing your request. Please try again.",
            },
            sources: [],
            response_time: 0,
            isError: true,
          });
          copilotStore.setChatLoading(false);
          copilotStore.setStopMessageId("");
          setStopReason("no_response_timeout");

          if (currentMessageId.current !== "") {
            try {
              await deleteMessage(currentMessageId.current, {
                is_deleted: true,
              });
            } catch (error) {
              console.error("Error deleting message:", error);
            }
          }
        } else {
          controllerRef.current.abort();
        }
        fallbackTimeoutRef.current = null;
      };

      fallbackTimeoutRef.current = setTimeout(fallbackMethod, 5 * 60 * 1000);

      try {
        const accessToken = localStorage.getItem("accessToken");

        await fetchEventSource(`${import.meta.env.VITE_API_URL}/chat-stream`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify(data),
          signal,
          openWhenHidden: true,
          onopen: async (response) => {
            if (response.ok) {
              return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          },
          onmessage(event) {
            try {
              const jsonData = JSON.parse(event.data);

              if (jsonData.conversation_id) {
                copilotStore.setCurrentConversationId(jsonData.conversation_id);
                if (onNewConversation) {
                  onNewConversation(jsonData.conversation_id);
                }

                queryClient.invalidateQueries({
                  predicate: (query) => query.queryKey[0] === "chatHistory",
                });
              }

              if (jsonData.internal_event) {
                copilotStore.setStopMessageId(
                  jsonData?.internal_event?.message_id
                );
                copilotStore.setParentId(jsonData?.internal_event?.parent_id);
                currentMessageId.current = jsonData?.internal_event?.message_id;
              } else if (jsonData?.data?.data?.assistant_error) {
                copilotStore.addMessage({
                  message_id: "retry_message",
                  message_type: "System",
                  data: { content: jsonData?.data?.data?.assistant_error },
                  sources: [],
                });
                if (fallbackTimeoutRef.current) {
                  clearTimeout(fallbackTimeoutRef.current);
                  fallbackTimeoutRef.current = setTimeout(
                    fallbackMethod,
                    5 * 60 * 1000
                  );
                }
              } else if (
                jsonData?.data?.response ||
                jsonData?.data?.response?.trim() === ""
              ) {
                if (fallbackTimeoutRef.current) {
                  clearTimeout(fallbackTimeoutRef.current);
                  fallbackTimeoutRef.current = null;
                }
                const sysResponse = jsonData.data.response;
                copilotStore.addMessage({
                  message_id: jsonData.data.message_id,
                  message_type: "System",
                  data: { content: sysResponse },
                  sources: createCitations(jsonData.data?.sources),
                });

                if (jsonData?.data?.response === "") {
                  deleteMessage(jsonData.data.message_id, {
                    is_deleted: true,
                  }).catch((error) => {
                    console.error("Error deleting message:", error);
                  });
                }

                copilotStore.setCurrentConversationId(
                  jsonData.data.conversation_id
                );

                copilotStore.setStopMessageId("");
                copilotStore.setParentId("");
                setMessageId(jsonData.data.message_id);
              }

              if (jsonData?.data?.error) {
                copilotStore.addMessage({
                  message_id: "error_message",
                  message_type: "System",
                  data: { content: jsonData?.data?.error },
                  sources: [],
                  isError: true,
                });

                if (fallbackTimeoutRef.current) {
                  clearTimeout(fallbackTimeoutRef.current);
                  fallbackTimeoutRef.current = null;
                }

                copilotStore.setChatLoading(false);
                copilotStore.setStopMessageId("");
              }
            } catch (error) {
              console.error("Error parsing JSON:", error);
              copilotStore.setStopMessageId("");
              copilotStore.setParentId("");
            }
          },
          onclose() {
            if (fallbackTimeoutRef.current) {
              clearTimeout(fallbackTimeoutRef.current);
              fallbackTimeoutRef.current = null;
            }

            copilotStore.setChatLoading(false);
            setStopReason("stop_sequence");
            copilotStore.setStopMessageId("");
            copilotStore.setParentId("");
          },
          onerror(error) {
            if (fallbackTimeoutRef.current) {
              clearTimeout(fallbackTimeoutRef.current);
              fallbackTimeoutRef.current = null;
            }

            console.error("EventSource error:", error);
            copilotStore.setChatLoading(false);
            throw error;
          },
        });
      } catch (error: any) {
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }

        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("API error:", error);
        }
        copilotStore.setChatLoading(false);
      }
    },
    [
      controllerRef,
      setChatLoading,
      setStopReason,
      copilotStore,
      copilotStore.currentConversationId,
    ]
  );

  const stopChatMessage = useCallback(async () => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }

    if (copilotStore?.stopMessageId) {
      try {
        await deleteMessage(copilotStore?.stopMessageId, { is_deleted: true });
        copilotStore.setStopMessageId("");
        copilotStore.setStoppedByUser(copilotStore?.messages);
      } catch (error) {
        console.error("Error deleting message:", error);
      }
    }
    if (controllerRef?.current) {
      controllerRef.current.abort();
    }
  }, [controllerRef, copilotStore?.stopMessageId]);

  return {
    sendChatMessage,
    stopChatMessage,
    messageId,
    fallbackTimeoutRef,
  };
};
